/**
 * Auth Context
 * Global authentication state management using Clerk
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import type { User, EmailLinkAuthDTO } from '@/dto';
import { createAuthRepository } from '@/persistence';
import { ClerkAuthRepository } from '@/persistence/clerk/ClerkAuthRepository';
import { clearRoleCache } from '@/shared/lib';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  sendSignInLink: (params: EmailLinkAuthDTO) => Promise<void>;
  signInWithLink: (email: string, emailLink: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const authRepo = createAuthRepository() as ClerkAuthRepository;

export function AuthProvider({ children }: AuthProviderProps) {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const clerk = useClerk();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize Clerk instance in repository
  useEffect(() => {
    if (clerk) {
      authRepo.setClerkInstance(clerk);
    }
  }, [clerk]);

  // Sync Clerk user state with our repository and local state
  useEffect(() => {
    if (clerkLoaded) {
      authRepo.setCurrentUser(clerkUser ?? null);
      const bridgedUser = authRepo.getCurrentUser();
      setUser(bridgedUser);
      setIsLoading(false);
    }
  }, [clerkUser, clerkLoaded]);

  const login = async (email: string, password: string): Promise<void> => {
    const user = await authRepo.signIn(email, password);
    setUser(user);
  };

  const signUp = async (email: string, password: string): Promise<void> => {
    const user = await authRepo.signUp(email, password);
    setUser(user);
  };

  const sendSignInLink = async (params: EmailLinkAuthDTO): Promise<void> => {
    await authRepo.sendSignInLinkToEmail(params);
  };

  const signInWithLink = async (email: string, emailLink: string): Promise<void> => {
    const user = await authRepo.signInWithEmailLink(email, emailLink);
    setUser(user);
  };

  const logout = async (): Promise<void> => {
    await authRepo.signOut();
    setUser(null);
    clearRoleCache(); // Clear cached role on logout
  };

  const getToken = async (): Promise<string | null> => {
    return await authRepo.getIdToken();
  };

  const sendPasswordReset = async (email: string): Promise<void> => {
    await authRepo.sendPasswordResetEmail(email);
  };

  const updatePassword = async (newPassword: string): Promise<void> => {
    await authRepo.updatePassword(newPassword);
  };

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    signUp,
    sendSignInLink,
    signInWithLink,
    logout,
    getToken,
    sendPasswordReset,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
